import { css, html, PropertyValues } from 'lit';
import { property } from 'lit/decorators.js';
import { GzRadioWebComponent } from './GzRadioWebComponent';
import { WEB_COMPONENT_GZ_RADIO } from '../../../webcomponents.constants';
import { FormFieldInput } from '../../gzForm/formField/component/FormFieldInput';
import { EventKeyManager } from '../../../utils/keys';
import { RadioEvent, RadioSelection, RadioSelectionKey } from '../types';

let groupCounter = 0;

export class GzRadioGroupWebComponent extends FormFieldInput<string | number> {
  /**
   * The `selected` option from the radio inputs.
   */
  @property({ type: String, attribute: true, reflect: true })
  selectedOption: string | number;

  @property({ type: String, attribute: true, reflect: true })
  groupName: string | null;

  private propagateDisabledToChildren = true;

  /**
   * Get all direct radio button children inside this radio group
   */
  private get radioButtons(): GzRadioWebComponent[] {
    // Select all <gz-radio> elements within this radio group
    const allRadios = this.querySelectorAll(WEB_COMPONENT_GZ_RADIO);

    // Convert NodeList to Array and filter out nested <gz-radio> elements
    return Array.from(allRadios).filter((radio: Element) => {
      // Find the closest <gz-radio-group> ancestor
      const closestGroup = radio.closest('gz-radio-group');

      // Include the radio if its closest <gz-radio-group> is this group
      return closestGroup === this;
    }) as GzRadioWebComponent[];
  }

  private get notDisabledRadioButtons(): GzRadioWebComponent[] {
    return this.radioButtons.filter((radio: GzRadioWebComponent) => !radio.disabled);
  }

  checkedChangedListener = e => {
    this.handleRadioGroupSelection(e.target.value);
  };

  static get styles() {
    return css`
      :host {
        box-sizing: border-box;
        margin-bottom: 8px;
        width: auto;
        height: auto;
        display: block;
      }
    `;
  }

  render() {
    return html` <slot></slot> `;
  }

  public setFormValue(value: string | number) {
    this.selectOption(value);
    this.updateRadioButtonsCheckedStates();
  }

  public getFormValue(): string | number {
    return this.selectedOption;
  }

  public setEnabled(isEnabled: boolean, propagateToChildren?: boolean): void {
    this.propagateDisabledToChildren = propagateToChildren ?? true;
    this.disabled = !isEnabled;
  }

  /**
   * When the component is added to the DOM add the radios change event listeners and update buttons states based on
   * currently selected option
   */
  connectedCallback(): void {
    super.connectedCallback();
    this.radioButtons.forEach((radio: GzRadioWebComponent) => {
      radio.addEventListener('click', this.checkedChangedListener);
    });
    this.updateRadioButtonsCheckedStates();
    this.updateRadioButtonsName();
    this.addEventListener('keydown', this.keydownHandler);
  }

  /**
   * Remove the event listeners when the component is removed from the DOM
   */
  disconnectedCallback() {
    super.disconnectedCallback();
    this.radioButtons.forEach((radio: GzRadioWebComponent) => {
      radio.removeEventListener('click', this.checkedChangedListener);
    });
    this.removeEventListener('keydown', this.keydownHandler);
  }

  updated(changedProperties: PropertyValues) {
    if (changedProperties.has('selectedOption')) {
      this.updateRadioButtonsCheckedStates();
    }

    if (changedProperties.has('disabled') && this.propagateDisabledToChildren) {
      this.updateRadioButtonsDisabledStates(this.disabled);
    }

    this.enableDisableRadioButtonsTabIndex();
  }

  private keydownHandler = (event: Event): void => {
    if (EventKeyManager.isLeft(event) || EventKeyManager.isUp(event)) {
      this.checkNextPreviousRadio(RadioSelectionKey.PREVIOUS_INDEX);
    }

    if (EventKeyManager.isRight(event) || EventKeyManager.isDown(event)) {
      this.checkNextPreviousRadio(RadioSelectionKey.NEXT_INDEX);
    }
  };

  private checkNextPreviousRadio(namespace: RadioSelectionKey): void {
    const currentSelection = this.retrieveCurrentSelection();
    const selectedRadio = this.notDisabledRadioButtons[currentSelection[namespace]];
    const { value } = selectedRadio;

    this.updateRadiosTabIndex();
    this.handleRadioGroupSelection(value);
    selectedRadio.focus();
  }

  private retrieveCurrentSelection(): RadioSelection {
    return new RadioSelection(this.notDisabledRadioButtons);
  }

  public selectOption(option: string | number) {
    this.selectedOption = option;
    this.emitSelectionEvent();
  }

  /**
   * When a radio button inside this group is checked, update the selectedOption property, update the buttons
   * checked/unchecked states and emit the selection event
   */
  private handleRadioGroupSelection(value: string | number) {
    /**
     * If the user clicks on a currently active radio, return;
     */
    if (value === this.selectedOption) {
      return;
    }

    this.selectOption(value);
    this.updateRadioButtonsCheckedStates();
    this.updateRadiosTabIndex();
  }

  /**
   * If one of the buttons value matches the currently selected option => set the button state to checked and
   * uncheck the other buttons.
   */
  private updateRadioButtonsCheckedStates() {
    this.radioButtons.forEach((radio: GzRadioWebComponent) => {
      radio.checked = radio.value === this.selectedOption;
    });
  }

  private updateRadioButtonsDisabledStates(disabled: boolean) {
    this.radioButtons.forEach((radio: GzRadioWebComponent) => {
      radio.disabled = disabled;
    });
  }

  private enableDisableRadioButtonsTabIndex() {
    if (this.disabled) {
      this.removeRadiosTabIndex();
    } else {
      this.updateRadiosTabIndex();
    }
  }

  private updateRadiosTabIndex(): void {
    const { radio: selectedRadio } = this.retrieveCurrentSelection();

    this.radioButtons.forEach((radio: GzRadioWebComponent) => (radio.tabIndex = radio !== selectedRadio ? -1 : 0));
  }

  private removeRadiosTabIndex(): void {
    this.radioButtons.forEach((radio: GzRadioWebComponent) => (radio.tabIndex = -1));
  }

  private updateRadioButtonsName() {
    const defaultGroupName = `radio-group-${groupCounter}`;
    groupCounter++;

    this.radioButtons.forEach((radio: GzRadioWebComponent) => {
      if (this.groupName) {
        radio.name = this.groupName;
        return;
      }

      if (!radio.name) {
        radio.name = defaultGroupName;
      }
    });
  }

  private emitSelectionEvent() {
    const event = new CustomEvent(RadioEvent.SELECTION_CHANGED, {
      detail: {
        selectedValue: this.selectedOption,
      },
      bubbles: true,
      composed: true,
    });
    this.dispatchEvent(event);
  }
}
